/**
 * Custom field rules for the Options Tape.
 *
 * Users compose rules over the sixteen fields the live tape carries (LIVE_TAPE_FIELDS). A rule
 * that maps onto a query parameter the server already accepts is pushed into the request, so it
 * applies to the whole match set in QuestDB. Anything else — "is not", contains on a non-ticker
 * field, price, spot, condition, exchange — has no server parameter and is applied to the page
 * that came back. The UI labels each rule with where it runs, so a client-side rule is never
 * mistaken for a filter over the full tape.
 */

import type { FlowTapeQuery } from '../../utils/flowTapeApi';
import { LIVE_TAPE_FIELDS } from '../../utils/liveTapeTiles';
import type { CustomFilter, FlowFieldDef } from '../../utils/rtlScannerFlowRows';

export { LIVE_TAPE_FIELDS };

export const TAPE_FIELD_BY_KEY: Record<string, FlowFieldDef> = LIVE_TAPE_FIELDS.reduce(
  (acc, f) => {
    acc[f.key] = f;
    return acc;
  },
  {} as Record<string, FlowFieldDef>,
);

type QKey = keyof FlowTapeQuery;

/** Where each (field, operator) pair lands on the server, when it lands anywhere. */
function serverTargets(rule: CustomFilter): { key: QKey; kind: 'min' | 'max' | 'eq' }[] | null {
  const v = rule.value.trim();
  if (!v) return null;
  const isNum = Number.isFinite(parseFloat(v));
  switch (rule.field) {
    case 'Ticker':
      if (rule.op === 'eq') return [{ key: 'ticker', kind: 'eq' }];
      if (rule.op === 'contains') return [{ key: 'search', kind: 'eq' }];
      return null;
    case 'Option Type':
      return rule.op === 'eq' ? [{ key: 'optionType', kind: 'eq' }] : null;
    case 'Side':
      return rule.op === 'eq' ? [{ key: 'side', kind: 'eq' }] : null;
    case 'Expiry Date':
      return rule.op === 'eq' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? [{ key: 'expiry', kind: 'eq' }] : null;
    case 'Strike':
    case 'DTE':
    case 'Premium': {
      if (!isNum) return null;
      const [lo, hi]: [QKey, QKey] =
        rule.field === 'Strike' ? ['minStrike', 'maxStrike'] : rule.field === 'DTE' ? ['minDte', 'maxDte'] : ['minPremium', 'maxPremium'];
      if (rule.op === 'gte') return [{ key: lo, kind: 'min' }];
      if (rule.op === 'lte') return [{ key: hi, kind: 'max' }];
      if (rule.op === 'eq') return [{ key: lo, kind: 'min' }, { key: hi, kind: 'max' }];
      return null;
    }
    case 'Volume':
      return rule.op === 'gte' && isNum ? [{ key: 'minSize', kind: 'min' }] : null;
    default:
      return null;
  }
}

function normValue(rule: CustomFilter): string {
  const v = rule.value.trim();
  if (rule.field === 'Ticker') return v.toUpperCase();
  if (rule.field === 'Option Type') return v.toUpperCase().slice(0, 1);
  if (rule.field === 'Side') return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
  return v;
}

export interface RulePlan {
  /** The bar's query with server-mappable rules folded in. */
  query: FlowTapeQuery;
  /** Rule ids that run in QuestDB. */
  server: Set<string>;
  /** Rules applied to the loaded page. */
  client: CustomFilter[];
}

/**
 * Fold rules into the query. Bounds take the stricter of the bar and the rule, which is what
 * ANDing them means. An exact-match rule that disagrees with a value already on the bar stays
 * client-side, so the result is the honest intersection (usually empty) rather than the rule
 * quietly overriding the bar.
 */
export function planRules(base: FlowTapeQuery, rules: CustomFilter[]): RulePlan {
  const query: FlowTapeQuery = { ...base };
  const server = new Set<string>();
  const client: CustomFilter[] = [];
  for (const rule of rules) {
    if (!rule.value.trim()) continue;
    const targets = serverTargets(rule);
    if (!targets) {
      client.push(rule);
      continue;
    }
    const value = normValue(rule);
    let conflict = false;
    for (const t of targets) {
      const cur = String(query[t.key] ?? '').trim();
      if (t.kind === 'eq' && cur && cur.toUpperCase() !== value.toUpperCase()) conflict = true;
      // Contains on ticker is ignored by the server while an exact ticker is set.
      if (t.key === 'search' && String(query.ticker ?? '').trim()) conflict = true;
    }
    if (conflict) {
      client.push(rule);
      continue;
    }
    for (const t of targets) {
      const cur = String(query[t.key] ?? '').trim();
      if (t.kind === 'eq' || !cur) {
        (query as Record<string, unknown>)[t.key] = value;
      } else {
        const a = parseFloat(cur);
        const b = parseFloat(value);
        const pick = t.kind === 'min' ? Math.max(a, b) : Math.min(a, b);
        (query as Record<string, unknown>)[t.key] = String(Number.isFinite(pick) ? pick : value);
      }
    }
    server.add(rule.id);
  }
  return { query, server, client };
}

function normExpiry(v: unknown): string {
  const s = String(v ?? '').trim();
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return s.slice(0, 10);
}

function matchesOne(raw: Record<string, unknown>, rule: CustomFilter): boolean {
  const def = TAPE_FIELD_BY_KEY[rule.field];
  if (!def) return true;
  const value = rule.value.trim();
  if (!value) return true;
  const cell = raw[rule.field];
  if (def.type === 'number') {
    const target = parseFloat(value);
    if (!Number.isFinite(target)) return true;
    const n = typeof cell === 'number' ? cell : parseFloat(String(cell ?? ''));
    if (!Number.isFinite(n)) return false;
    switch (rule.op) {
      case 'gte': return n >= target;
      case 'lte': return n <= target;
      case 'eq': return n === target;
      case 'ne': return n !== target;
      default: return true;
    }
  }
  const cellStr = (rule.field === 'Expiry Date' ? normExpiry(cell) : String(cell ?? '')).toLowerCase();
  const wanted = value.toLowerCase();
  switch (rule.op) {
    case 'contains': return cellStr.includes(wanted);
    case 'eq': return cellStr === wanted;
    case 'ne': return cellStr !== wanted;
    default: return true;
  }
}

/** All rules are ANDed. A record without a field a rule names cannot satisfy that rule. */
export function matchesRules(raw: Record<string, unknown> | undefined, rules: CustomFilter[]): boolean {
  if (!rules.length) return true;
  if (!raw) return false;
  return rules.every((r) => matchesOne(raw, r));
}

/** Fields a contract-board row carries; rules on any other field only make sense per print. */
export const CONTRACT_ROW_FIELDS = new Set(['Ticker', 'Contract', 'Option Type', 'Strike', 'Expiry Date', 'Premium', 'Volume', 'DTE']);
